import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useT } from '../../renderer/TextsProvider.js';
import { cssColor } from '../../sections/_shared/settingValue.js';
import { ALIGN_OPTIONS, marginFields, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, num, str, visibilityCss } from '../_shared/blockRender.js';
import { useWelcomeForm } from '../_shared/welcomeForm.js';
/**
 * `welcome-consent` — la mention de consentement de la fenêtre de bienvenue (feature 0074, lot B).
 *
 * Pas un bloc `text` avec une petite police : la mention peut porter une CASE À COCHER obligatoire, et
 * c'est une pièce du formulaire, pas de la décoration. Là où la loi l'exige (RGPD : un consentement doit
 * être un acte positif, pas une case pré-cochée), la vendeuse allume la case et l'envoi est refusé tant
 * qu'elle n'est pas cochée — par le navigateur, sans une ligne de JavaScript.
 *
 * Le nom de la case est `consent` : si elle existe, l'îlot la trouve dans le `FormData` de l'envoi.
 */
export const welcomeConsentBlockSchema = {
    type: 'welcome-consent',
    name: 'Consent note',
    fields: [
        visibilityField,
        {
            id: 'text',
            type: 'textarea',
            label: 'Text',
            info: 'Leave empty to keep your shop’s own wording, in your shop’s language.',
            default: '',
        },
        {
            id: 'showCheckbox',
            type: 'checkbox',
            label: 'Ask for a tick',
            info: 'Shows a checkbox in front of the text. Required where consent must be an explicit act.',
            default: false,
        },
        {
            id: 'required',
            type: 'checkbox',
            label: 'The tick is required',
            default: true,
            visibleWhen: { field: 'showCheckbox', equals: true },
        },
        {
            id: 'textColor',
            type: 'color',
            label: 'Text',
            group: 'Colors',
            default: { ref: 'custom', value: '' },
            info: 'Empty = a faded version of the popup’s own text colour.',
        },
        {
            id: 'fontSize',
            type: 'range',
            label: 'Font size',
            group: 'Layout',
            min: 9,
            max: 16,
            step: 1,
            default: 12,
            unit: 'px',
        },
        {
            id: 'alignment',
            type: 'select',
            label: 'Alignment',
            group: 'Layout',
            default: 'center',
            options: ALIGN_OPTIONS,
        },
        ...marginFields(6, 0),
    ],
};
const WelcomeConsentBlock = ({ block }) => {
    const bs = block.settings;
    const cls = `aqx-wpc-${block.id}`;
    const t = useT();
    const { busy } = useWelcomeForm();
    const text = str(bs.text).trim() || t('welcome_popup.consent');
    const withBox = bs.showCheckbox === true;
    const align = str(bs.alignment, 'center');
    const rowStyle = {
        display: 'flex',
        gap: '8px',
        alignItems: 'flex-start',
        justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
        textAlign: align,
        // Vide = une version atténuée de l'encre de la fenêtre. `color-mix` la dérive de la couleur que la
        // carte a réellement posée, donc la mention reste lisible sur un fond clair comme sur un fond sombre.
        color: cssColor(bs.textColor, 'color-mix(in srgb, currentColor 62%, transparent)'),
        fontSize: `${num(bs.fontSize, 12)}px`,
        lineHeight: 1.45,
        ...marginStyle(bs, 6, 0),
    };
    const css = visibilityCss(cls, bs.visibility);
    return (_jsxs("div", { className: cls, style: rowStyle, children: [css ? _jsx("style", { dangerouslySetInnerHTML: { __html: css } }) : null, withBox ? (_jsx("input", { type: "checkbox", name: "consent", value: "yes", required: bs.required !== false, disabled: busy, "aria-label": text, style: { marginBlockStart: '2px', flex: '0 0 auto' } })) : null, _jsx("span", { children: text })] }));
};
export const welcomeConsentBlock = {
    type: 'welcome-consent',
    schema: welcomeConsentBlockSchema,
    Component: WelcomeConsentBlock,
    meta: { label: 'Consent note', icon: 'shield' },
};
//# sourceMappingURL=index.js.map